import { useMemo, useState } from 'react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { categoryColor, rampColor, type VizColors } from '../theme';

/**
 * Marks for the automatically composed dashboard.
 *
 * These follow the same rules as the hand-built charts — recessive grid, 2px
 * lines, rounded data-ends, a tooltip on everything, and never a second y-axis
 * — with three additions the auto-layout needs:
 *
 *   - **Colour comes from a fixed categorical order, not from rank.** Every
 *     chart on the page assigns a hue by the entity's stable position, so
 *     filtering out the leader does not repaint the survivors and force the
 *     reader to re-learn the legend.
 *   - **A gap is drawn as a gap.** `null` means no rows at that point, and
 *     joining across it would draw a line through data that does not exist.
 *   - **Everything is clickable.** Cross-filtering is the point of the page, so
 *     a mark is a control, and the cursor says so.
 */

const AXIS_TICK = { fontSize: 12 };
const TOOLTIP_SHADOW = '0 2px 10px rgba(0,0,0,0.14)';

export function formatCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${(value / 1000).toFixed(1)}k`;
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

export function truncate(value: string, max = 22): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

interface TooltipRow {
  name?: string | number;
  value?: number | string | (number | string)[];
  color?: string;
}

function Surface({
  colors,
  title,
  rows,
}: {
  colors: VizColors;
  title: string;
  rows: readonly { label: string; value: string; color?: string }[];
}) {
  return (
    <div
      style={{
        background: colors.surface,
        border: `1px solid ${colors.axis}`,
        borderRadius: 7,
        padding: '8px 10px',
        fontSize: 12,
        color: colors.ink,
        boxShadow: TOOLTIP_SHADOW,
        maxWidth: 280,
      }}
    >
      <div style={{ fontWeight: 620, marginBottom: rows.length > 0 ? 4 : 0 }}>{title}</div>
      {rows.map((row, index) => (
        <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {row.color !== undefined && (
            <span
              style={{ width: 8, height: 8, borderRadius: 2, background: row.color, flex: '0 0 auto' }}
            />
          )}
          {/* Labels wear ink tokens, never the series colour — the swatch beside
              them already carries the identity. */}
          <span style={{ color: colors.muted }}>{row.label}</span>
          <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{row.value}</span>
        </div>
      ))}
    </div>
  );
}

function ChartTooltip({
  active,
  label,
  payload,
  colors,
}: {
  active?: boolean;
  label?: string | number;
  payload?: TooltipRow[];
  colors: VizColors;
}) {
  if (active !== true || payload === undefined || payload.length === 0) return null;

  return (
    <Surface
      colors={colors}
      title={String(label ?? '')}
      rows={payload
        // A null series contributes no row: printing "Region B: —" on every
        // hover buries the series that do have a value at that point.
        .filter((row) => row.value !== null && row.value !== undefined)
        .map((row) => ({
          label: String(row.name ?? ''),
          value:
            typeof row.value === 'number'
              ? row.value.toLocaleString('en-US', { maximumFractionDigits: 2 })
              : String(row.value),
          color: row.color,
        }))}
    />
  );
}

// ---------------------------------------------------------------------------
// Trend
// ---------------------------------------------------------------------------

export interface SeriesPoint {
  readonly key: string;
  readonly values: readonly (number | null)[];
}

export function MultiTrend({
  series,
  points,
  colors,
  height = 280,
  onSelect,
}: {
  series: readonly string[];
  points: readonly SeriesPoint[];
  colors: VizColors;
  height?: number;
  onSelect?: (key: string) => void;
}) {
  const data = useMemo(
    () =>
      points.map((point) => {
        const row: Record<string, string | number | null> = { key: point.key };
        series.forEach((name, index) => {
          row[name] = point.values[index] ?? null;
        });
        return row;
      }),
    [points, series],
  );

  if (data.length === 0) return <p className="muted">No rows match.</p>;

  const single = series.length === 1;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart
        data={data}
        margin={{ top: 8, right: 8, bottom: 4, left: 0 }}
        onClick={(event) => {
          const key = (event as { activeLabel?: string }).activeLabel;
          if (key !== undefined && onSelect !== undefined) onSelect(key);
        }}
        style={onSelect === undefined ? undefined : { cursor: 'pointer' }}
      >
        <CartesianGrid stroke={colors.grid} vertical={false} />
        <XAxis dataKey="key" tick={AXIS_TICK} stroke={colors.axis} minTickGap={24} />
        <YAxis tick={AXIS_TICK} stroke={colors.axis} tickFormatter={formatCompact} width={58} />
        <Tooltip content={<ChartTooltip colors={colors} />} />
        {/* One series names itself in the block title; two or more always need
            a legend, because identity must never be colour-alone. */}
        {!single && <Legend wrapperStyle={{ fontSize: 12 }} />}

        {single && (
          <Area
            dataKey={series[0]!}
            stroke="none"
            fill={colors.band}
            isAnimationActive={false}
            connectNulls={false}
          />
        )}

        {series.map((name, index) => (
          <Line
            key={name}
            type="monotone"
            dataKey={name}
            name={name}
            stroke={categoryColor(colors, index)}
            strokeWidth={2}
            dot={data.length <= 40 ? { r: 4, strokeWidth: 0, fill: categoryColor(colors, index) } : false}
            activeDot={{ r: 5 }}
            // A missing bucket is a gap, not zero. Joining across it draws a
            // line through data that was never observed.
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------
// Bars
// ---------------------------------------------------------------------------

export interface Point {
  readonly key: string;
  readonly value: number;
  readonly count: number;
}

export function Bars({
  points,
  other,
  measureLabel,
  colors,
  orientation = 'vertical',
  height = 280,
  selected,
  onSelect,
}: {
  points: readonly Point[];
  other?: Point | null;
  measureLabel: string;
  colors: VizColors;
  orientation?: 'vertical' | 'horizontal';
  height?: number;
  selected?: string | null;
  onSelect?: (key: string) => void;
}) {
  // The folded remainder is drawn, not dropped: a chart that silently omits
  // the tail misrepresents the total it appears to show.
  const data = useMemo(
    () => [...points, ...(other != null ? [other] : [])].map((point) => ({ ...point, short: truncate(point.key) })),
    [points, other],
  );

  if (data.length === 0) return <p className="muted">No rows match.</p>;

  const horizontal = orientation === 'horizontal';
  const chartHeight = horizontal ? Math.max(height, data.length * 30 + 40) : height;

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart
        data={data}
        layout={horizontal ? 'vertical' : 'horizontal'}
        margin={{ top: 8, right: 16, bottom: 4, left: horizontal ? 8 : 0 }}
      >
        <CartesianGrid stroke={colors.grid} vertical={horizontal} horizontal={!horizontal} />
        {horizontal ? (
          <>
            <XAxis type="number" tick={AXIS_TICK} stroke={colors.axis} tickFormatter={formatCompact} />
            {/* Category names are words. Rotating them under a vertical axis is
                the single most common unreadable chart, so long lists lie down. */}
            <YAxis
              type="category"
              dataKey="short"
              tick={AXIS_TICK}
              stroke={colors.axis}
              width={130}
              interval={0}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey="short"
              tick={AXIS_TICK}
              stroke={colors.axis}
              interval={0}
              angle={data.length > 6 ? -30 : 0}
              textAnchor={data.length > 6 ? 'end' : 'middle'}
              height={data.length > 6 ? 62 : 30}
            />
            <YAxis tick={AXIS_TICK} stroke={colors.axis} tickFormatter={formatCompact} width={58} />
          </>
        )}
        <Tooltip cursor={{ fill: colors.grid, opacity: 0.45 }} content={<ChartTooltip colors={colors} />} />
        <Bar
          dataKey="value"
          name={measureLabel}
          radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
          maxBarSize={horizontal ? 26 : 54}
          onClick={(entry: unknown) => {
            const key = (entry as { key?: string } | undefined)?.key;
            if (key !== undefined && onSelect !== undefined) onSelect(key);
          }}
          cursor={onSelect === undefined ? undefined : 'pointer'}
          isAnimationActive={false}
        >
          {data.map((entry) => (
            <Cell
              key={entry.key}
              // "Other" is not an entity, so it does not get an entity hue.
              fill={entry.key.startsWith('Other') || entry === other ? colors.other : colors.categorical[0]}
              // Selection dims the rest rather than recolouring the chosen bar,
              // so the colour still means what the legend says it means.
              fillOpacity={selected == null || selected === entry.key ? 1 : 0.28}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------
// Donut
// ---------------------------------------------------------------------------

export function Donut({
  points,
  other,
  total,
  totalLabel,
  colors,
  height = 280,
  onSelect,
}: {
  points: readonly Point[];
  other?: Point | null;
  total: number;
  totalLabel: string;
  colors: VizColors;
  height?: number;
  onSelect?: (key: string) => void;
}) {
  const data = useMemo(
    () => [...points, ...(other != null ? [{ ...other, key: `Other (${other.count})` }] : [])],
    [points, other],
  );

  if (data.length === 0) return <p className="muted">No rows match.</p>;

  return (
    <div style={{ position: 'relative' }}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Tooltip
            content={({ active, payload }) => {
              const slice = payload?.[0]?.payload as Point | undefined;
              if (active !== true || slice === undefined) return null;
              const share = total === 0 ? 0 : (slice.value / total) * 100;
              return (
                <Surface
                  colors={colors}
                  title={slice.key}
                  rows={[
                    { label: 'Value', value: slice.value.toLocaleString('en-US', { maximumFractionDigits: 2 }) },
                    { label: 'Share', value: `${share.toFixed(1)}%` },
                    { label: 'Rows', value: slice.count.toLocaleString('en-US') },
                  ]}
                />
              );
            }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Pie
            data={data as unknown as Record<string, unknown>[]}
            dataKey="value"
            nameKey="key"
            innerRadius="52%"
            outerRadius="76%"
            paddingAngle={2}
            // A 2px surface-coloured ring separates adjacent fills so two
            // similar slices never read as one wedge.
            stroke={colors.surface}
            strokeWidth={2}
            isAnimationActive={false}
            onClick={(entry: unknown) => {
              const key = (entry as { key?: string } | undefined)?.key;
              if (key !== undefined && onSelect !== undefined && !key.startsWith('Other')) onSelect(key);
            }}
            cursor={onSelect === undefined ? undefined : 'pointer'}
          >
            {data.map((entry, index) => (
              <Cell
                key={entry.key}
                fill={entry.key.startsWith('Other') ? colors.other : categoryColor(colors, index)}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      {/* The hole is the natural place for the whole the parts add up to. */}
      <div className="donut-centre" aria-hidden>
        <span className="donut-total">{formatCompact(total)}</span>
        <span className="donut-label">{totalLabel}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Heatmap
// ---------------------------------------------------------------------------

export interface HeatmapCell {
  readonly row: number;
  readonly column: number;
  readonly value: number | null;
}

export function Heatmap({
  rowKeys,
  columnKeys,
  cells,
  min,
  max,
  colors,
  onSelect,
}: {
  rowKeys: readonly string[];
  columnKeys: readonly string[];
  cells: readonly HeatmapCell[];
  min: number;
  max: number;
  colors: VizColors;
  onSelect?: (row: string, column: string) => void;
}) {
  const [hover, setHover] = useState<HeatmapCell | null>(null);

  const grid = useMemo(() => {
    const lookup = new Map<string, HeatmapCell>();
    for (const cell of cells) lookup.set(`${cell.row}:${cell.column}`, cell);
    return lookup;
  }, [cells]);

  if (rowKeys.length === 0 || columnKeys.length === 0) {
    return <p className="muted">No rows match.</p>;
  }

  return (
    <div className="heatmap">
      <div
        className="heatmap-grid"
        style={{ gridTemplateColumns: `minmax(90px, 150px) repeat(${columnKeys.length}, minmax(34px, 1fr))` }}
      >
        <div className="heatmap-corner" />
        {columnKeys.map((key) => (
          <div className="heatmap-column-label" key={key} title={key}>
            {truncate(key, 10)}
          </div>
        ))}

        {rowKeys.map((rowKey, rowIndex) => (
          <Row
            key={rowKey}
            rowKey={rowKey}
            rowIndex={rowIndex}
            columnKeys={columnKeys}
            grid={grid}
            min={min}
            max={max}
            colors={colors}
            onHover={setHover}
            onSelect={onSelect}
          />
        ))}
      </div>

      <div className="heatmap-legend">
        <span className="muted">{formatCompact(min)}</span>
        {colors.sequential.map((step) => (
          <span key={step} className="heatmap-swatch" style={{ background: step }} />
        ))}
        <span className="muted">{formatCompact(max)}</span>
        {/* An empty cell carries no fill at all. Giving it the ramp's lowest
            step would claim the value was measured and found to be nothing. */}
        <span className="heatmap-swatch empty" />
        <span className="muted">no data</span>
      </div>

      <p className="heatmap-readout" aria-live="polite">
        {hover === null
          ? 'Hover a cell for its value.'
          : `${rowKeys[hover.row]} · ${columnKeys[hover.column]} — ${
              hover.value === null ? 'no rows' : hover.value.toLocaleString('en-US', { maximumFractionDigits: 2 })
            }`}
      </p>
    </div>
  );
}

function Row({
  rowKey,
  rowIndex,
  columnKeys,
  grid,
  min,
  max,
  colors,
  onHover,
  onSelect,
}: {
  rowKey: string;
  rowIndex: number;
  columnKeys: readonly string[];
  grid: Map<string, HeatmapCell>;
  min: number;
  max: number;
  colors: VizColors;
  onHover: (cell: HeatmapCell | null) => void;
  onSelect?: (row: string, column: string) => void;
}) {
  return (
    <>
      <div className="heatmap-row-label" title={rowKey}>
        {truncate(rowKey, 18)}
      </div>
      {columnKeys.map((columnKey, columnIndex) => {
        const cell = grid.get(`${rowIndex}:${columnIndex}`) ?? {
          row: rowIndex,
          column: columnIndex,
          value: null,
        };
        const fill = rampColor(colors, cell.value, min, max);

        return (
          <button
            key={columnKey}
            type="button"
            className={fill === null ? 'heatmap-cell empty' : 'heatmap-cell'}
            style={fill === null ? undefined : { background: fill }}
            onMouseEnter={() => onHover(cell)}
            onFocus={() => onHover(cell)}
            onMouseLeave={() => onHover(null)}
            onBlur={() => onHover(null)}
            onClick={() => onSelect?.(rowKey, columnKey)}
            // Screen readers get the value as text; colour is never the only
            // channel carrying it.
            aria-label={`${rowKey}, ${columnKey}: ${
              cell.value === null ? 'no data' : cell.value.toLocaleString('en-US')
            }`}
          />
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Distribution
// ---------------------------------------------------------------------------

export function Histogram({
  bins,
  median,
  mean,
  colors,
  height = 260,
}: {
  bins: readonly { from: number; to: number; count: number }[];
  median: number;
  mean: number;
  colors: VizColors;
  height?: number;
}) {
  const data = useMemo(
    () => bins.map((bin) => ({ label: formatCompact(bin.from), count: bin.count, from: bin.from })),
    [bins],
  );

  if (data.length === 0) return <p className="muted">No rows match.</p>;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
        <CartesianGrid stroke={colors.grid} vertical={false} />
        <XAxis dataKey="label" tick={AXIS_TICK} stroke={colors.axis} minTickGap={18} />
        <YAxis tick={AXIS_TICK} stroke={colors.axis} width={48} />
        <Tooltip cursor={{ fill: colors.grid, opacity: 0.45 }} content={<ChartTooltip colors={colors} />} />
        {/* Both markers, labelled. Their distance apart is the whole point of
            showing a distribution rather than an average. */}
        <ReferenceLine
          x={formatCompact(median)}
          stroke={colors.categorical[3]}
          strokeDasharray="4 3"
          label={{ value: 'median', position: 'top', fontSize: 10, fill: colors.muted }}
        />
        <ReferenceLine
          x={formatCompact(mean)}
          stroke={colors.categorical[1]}
          strokeDasharray="4 3"
          label={{ value: 'mean', position: 'insideTopRight', fontSize: 10, fill: colors.muted }}
        />
        <Bar dataKey="count" name="Rows" fill={colors.categorical[0]} radius={[3, 3, 0, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------
// Correlation
// ---------------------------------------------------------------------------

export function Scatter2({
  points,
  xLabel,
  yLabel,
  colors,
  height = 260,
}: {
  points: readonly { x: number; y: number }[];
  xLabel: string;
  yLabel: string;
  colors: VizColors;
  height?: number;
}) {
  if (points.length === 0) return <p className="muted">No rows match.</p>;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ScatterChart margin={{ top: 8, right: 12, bottom: 18, left: 0 }}>
        <CartesianGrid stroke={colors.grid} />
        <XAxis
          type="number"
          dataKey="x"
          name={xLabel}
          tick={AXIS_TICK}
          stroke={colors.axis}
          tickFormatter={formatCompact}
          label={{ value: xLabel, position: 'insideBottom', offset: -10, fontSize: 11, fill: colors.muted }}
        />
        <YAxis
          type="number"
          dataKey="y"
          name={yLabel}
          tick={AXIS_TICK}
          stroke={colors.axis}
          tickFormatter={formatCompact}
          width={58}
        />
        <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<ChartTooltip colors={colors} />} />
        <Scatter
          data={points as { x: number; y: number }[]}
          fill={colors.categorical[0]}
          fillOpacity={0.55}
          isAnimationActive={false}
        />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------
// Forecast
// ---------------------------------------------------------------------------

export function ForecastBand({
  history,
  points,
  colors,
  height = 280,
}: {
  history: readonly { key: string; value: number }[];
  points: readonly { key: string; value: number; lower: number; upper: number }[];
  colors: VizColors;
  height?: number;
}) {
  const data = useMemo(() => {
    const rows: Record<string, string | number | null | [number, number]>[] = history.map((point) => ({
      key: point.key,
      actual: point.value,
      projected: null,
      band: null,
    }));

    // The join point carries both series, or the projection appears to start
    // from a gap rather than from where the history ended.
    const last = history[history.length - 1];
    if (last !== undefined && rows.length > 0) {
      rows[rows.length - 1]!['projected'] = last.value;
      rows[rows.length - 1]!['band'] = [last.value, last.value];
    }

    for (const point of points) {
      rows.push({ key: point.key, actual: null, projected: point.value, band: [point.lower, point.upper] });
    }
    return rows;
  }, [history, points]);

  if (data.length === 0) return <p className="muted">Not enough history to project.</p>;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
        <CartesianGrid stroke={colors.grid} vertical={false} />
        <XAxis dataKey="key" tick={AXIS_TICK} stroke={colors.axis} minTickGap={28} />
        <YAxis tick={AXIS_TICK} stroke={colors.axis} tickFormatter={formatCompact} width={58} />
        <Tooltip content={<ChartTooltip colors={colors} />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Area
          dataKey="band"
          name="Prediction interval"
          stroke="none"
          fill={colors.band}
          isAnimationActive={false}
          connectNulls={false}
        />
        <Line
          type="monotone"
          dataKey="actual"
          name="Actual"
          stroke={colors.categorical[0]}
          strokeWidth={2}
          dot={false}
          connectNulls={false}
          isAnimationActive={false}
        />
        {/* Dashed, because it did not happen. A solid projection reads as
            history to anyone who does not check the legend. */}
        <Line
          type="monotone"
          dataKey="projected"
          name="Projected"
          stroke={colors.categorical[2]}
          strokeWidth={2}
          strokeDasharray="5 4"
          dot={false}
          connectNulls={false}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
